import { Link } from 'react-router-dom';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { ExitToAppRounded as ExitToAppIcon } from '@material-ui/icons';

import StatusChip from '../../../shared/components/Chips/StatusChip.component';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';
import { ADMIN_SEGMENTS_APP_PATHS } from '../../constants/segments-app-paths.constants';
import { Segment, SegmentSortColumn, SegmentSortDirection } from '../../types/segment.type';
import SegmentCategoryChip from '../Chips/SegmentCategoryChip.component';

interface SegmentsTableHeader {
  key: string;
  label: string;
  ariaLabel?: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
  sortColumn?: SegmentSortColumn;
}

interface SegmentsTableProps {
  segments: Segment[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  sortBy: SegmentSortColumn;
  sortDirection: SegmentSortDirection;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSortChange: (column: SegmentSortColumn) => void;
}

const TABLE_HEADERS: SegmentsTableHeader[] = [
  { key: 'actions', label: '', ariaLabel: 'Actions', minWidth: 50, align: 'center' },
  { key: 'name', label: 'Nom', minWidth: 250, align: 'left', sortColumn: 'name' },
  { key: 'category', label: 'Catégorie', minWidth: 150, align: 'left', sortColumn: 'category' },
  { key: 'amount', label: 'Taux', minWidth: 100, align: 'right', sortColumn: 'amount' },
  { key: 'status', label: 'Statut', minWidth: 100, align: 'center' },
];

const COLUMN_COUNT = TABLE_HEADERS.length;

const formatRate = (amount: number) => `${String(amount).replace('.', ',')} %`;

function SegmentsTableHeaderCell({
  header,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  header: SegmentsTableHeader;
  sortBy: SegmentSortColumn;
  sortDirection: SegmentSortDirection;
  onSortChange: (column: SegmentSortColumn) => void;
}) {
  const { sortColumn } = header;
  const isSorted = sortColumn === sortBy;

  return (
    <TableCell
      aria-label={header.ariaLabel}
      align={header.align}
      sortDirection={isSorted ? sortDirection : false}
      style={{ minWidth: header.minWidth, fontWeight: 'bold' }}
    >
      {sortColumn ? (
        <TableSortLabel
          active={isSorted}
          direction={isSorted ? sortDirection : 'asc'}
          onClick={() => onSortChange(sortColumn)}
        >
          {header.label}
        </TableSortLabel>
      ) : (
        header.label
      )}
    </TableCell>
  );
}

function SegmentRow({ segment }: { segment: Segment }) {
  return (
    <TableRow hover tabIndex={-1}>
      <TableCell align="center">
        <IconButton
          size="small"
          aria-label="voir"
          component={Link}
          to={ADMIN_SEGMENTS_APP_PATHS.SEGMENT_DETAILS(segment.id)}
        >
          <ExitToAppIcon />
        </IconButton>
      </TableCell>
      <TableCell align="left">{segment.name}</TableCell>
      <TableCell align="left">
        <SegmentCategoryChip category={segment.category} />
      </TableCell>
      <TableCell align="right">{formatRate(segment.amount)}</TableCell>
      <TableCell align="center">
        <StatusChip isActive={segment.isActive} archived={Boolean(segment.archivedAt)} />
      </TableCell>
    </TableRow>
  );
}

export function SegmentsTable({
  segments,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  sortBy,
  sortDirection,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}: SegmentsTableProps) {
  return (
    <Paper>
      <TableContainer style={{ maxHeight: '75svh' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <SegmentsTableHeaderCell
                  key={header.key}
                  header={header}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSortChange={onSortChange}
                />
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={COLUMN_COUNT} />}
          {!isLoading && (
            <TableBody>
              {segments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} style={{ textAlign: 'center' }}>
                    Aucun segment trouvé
                  </TableCell>
                </TableRow>
              )}
              {segments.map((segment) => (
                <SegmentRow key={segment.id} segment={segment} />
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Paper>
  );
}
